import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Module, Action, Mutation, VuexModule } from 'vuex-module-decorators';

export interface NewsObject {
  id: number;
  titleEn: string;
  titleAr: string;
  descriptionEn: string;
  descriptionAr: string;
  thumbnail: string;
  isActive: boolean;
  date: string;
  readTime: number;
  external_link: string;
}

export interface NewsListInfo {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  isNext: boolean;
  previous: number;
  news: Array<NewsObject>;
  errors: unknown;
}

@Module
export default class NewsModule extends VuexModule implements NewsListInfo {
  news = [] as Array<NewsObject>;
  totalItems = 0;
  currentPage = 1;
  pageSize = 10;
  previous = 0;
  isNext = true;
  errors = {};

  /**
   * Get News list
   * @returns Array<NewsObject>
   */
  get newsList(): Array<NewsObject> {
    return this.news;
  }

  /**
   * Get total items
   * @returns number
   */
  get newsListCount(): number {
    return this.totalItems;
  }

  @Mutation
  [Mutations.SET_NEWS](data) {
    this.news = data.data;
    this.totalItems = data.totalItems;
    this.currentPage = data.currentPage;
    this.pageSize = data.pageSize;
    this.isNext = data.isNext;
    this.previous = data.previous;
  }

  @Mutation
  [Mutations.SET_NEWS_CURRENT_PAGE](page) {
    this.currentPage = page;
  }

  @Mutation
  [Mutations.RESET_NEWS_LIST]() {
    this.news = [] as Array<NewsObject>;
    this.totalItems = 0;
    this.currentPage = 1;
    this.pageSize = 10;
    this.previous = 0;
    this.isNext = true;
    this.errors = {};
  }

  @Action
  [Actions.RESET_NEWS_STORE]() {
    this.context.commit(Mutations.RESET_NEWS_LIST);
  }

  @Action
  [Actions.CREATE_NEWS](data) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/news', data)
      .then((response) => {
        this.context.commit(Mutations.RESET_NEWS_LIST);
        return response.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.PULL_URL_NEWS](data) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/news/pull-meta-url', data)
      .then(({ data }) => {
        this.context.commit(Mutations.RESET_NEWS_LIST);
        return data.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.DELETE_NEWS](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.delete('/news', id)
      .then(() => {
        this.context.commit(Mutations.RESET_NEWS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPDATE_NEWS]({ id, data }) {
    ApiService.setAuthorizationHeader();
    return ApiService.update('/news', id, data)
      .then(() => {
        this.context.commit(Mutations.RESET_NEWS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPDATE_NEWS_CURRENT_PAGE](val) {
    this.context.commit(Mutations.SET_NEWS_CURRENT_PAGE, val);
  }

  @Action
  [Actions.GET_ONE_NEWS](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/news', id);
  }

  @Action
  [Actions.GET_NEWS]() {
    ApiService.setAuthorizationHeader();
    return ApiService.query('/news', {
      params: { page: this.currentPage },
    })
      .then(({ data }) => {
        this.context.commit(Mutations.SET_NEWS, data);
      })
      .catch(({ response }) => {
        console.error(response);
        // this.context.commit(Mutations.SET_ERROR, {
        //   [response.statusText]: [response.data.message],
        // });
      });
  }

  @Action
  [Actions.UPLOAD_NEWS_THUMBNAIL]({ data, id }) {
    ApiService.setAuthorizationHeader();
    ApiService.setMultipartHeader();
    return ApiService.post(`/news/uploadImage/${id}`, data)
      .then(({ data }) => {
        return data.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }
}
